<template>
 <div class="teacher-signups-tab">
 <!-- Header -->
 <div class="flex items-center justify-between mb-4">
 <span class="text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark">
 {{ signups.length }} {{ view === 'waitlist' ? 'on waitlist' : `signup${signups.length !== 1 ? 's' : ''}` }}
 </span>
 <div class="flex items-center gap-3">
 <select
 v-model="view"
 class="text-xs px-1.5 py-0.5 rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-secondary dark:text-fg-secondary-dark focus:outline-none focus:ring-2 focus:ring-blue-500"
 >
 <option value="open">Open</option>
 <option value="waitlist">Waitlist</option>
 </select>
 <button @click="load" :disabled="isLoading" class="text-fg-faint dark:text-fg-secondary-dark" title="Refresh">
 <ArrowPathIcon :class="['h-3.5 w-3.5', isLoading ? 'animate-spin' : '']" />
 </button>
 </div>
 </div>

 <!-- Loading skeleton -->
 <div v-if="isLoading && signups.length === 0" class="card text-center py-12">
 <div class="animate-pulse">
 <div class="h-4 w-48 bg-stroke dark:bg-stroke-dark rounded mx-auto mb-4"></div>
 <div class="h-3 w-32 bg-stroke dark:bg-stroke-dark rounded mx-auto"></div>
 </div>
 </div>

 <!-- Error -->
 <div v-else-if="error" class="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
 <div class="flex items-center">
 <ExclamationTriangleIcon class="h-5 w-5 text-red-500 dark:text-red-400 mr-3 flex-shrink-0" />
 <p class="font-medium text-red-800 dark:text-red-300">Failed to load signups</p>
 <button @click="load" class="ml-auto text-sm px-3 py-1.5 rounded-md border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-dark text-fg-secondary dark:text-fg-secondary-dark">Retry</button>
 </div>
 </div>

 <!-- Empty -->
 <!-- Nothing here but check-ins (StudentCheckIns, above): no "all caught up" under them -->
 <div v-else-if="signups.length === 0 && !(view === 'open' && badges.signups)" class="card text-center py-12">
 <AllCaughtUp v-if="view !== 'waitlist'" />
 <template v-else>
 <UserPlusIcon class="h-12 w-12 mx-auto text-stroke-strong dark:text-stroke-strong-dark mb-3" />
 <p class="text-fg-muted dark:text-fg-muted-dark">No waitlisted students</p>
 </template>
 </div>

 <!-- Groups -->
 <div v-else class="space-y-6">
 <section v-for="g in groups" :key="g.key">
 <h3 class="text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-widest mb-2">
 {{ g.title }} ({{ g.items.length }})
 </h3>
 <div class="space-y-3">
 <TeacherSignupCard v-for="s in g.items" :key="s.id" :signup="s" @updated="load" />
 </div>
 </section>
 </div>
 </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ArrowPathIcon, ExclamationTriangleIcon, UserPlusIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { useDemoMode } from '../../composables/useDemoMode'
import TeacherSignupCard from '../common/TeacherSignupCard.vue'
import AllCaughtUp from '../common/AllCaughtUp.vue'
import { useTeacherBadges } from '../../composables/useTeacherBadges.js'

// Teacher-facing signups (backend: api/endpoints/teacher_signups.py). Demo Mode's
// "preview as teacher" works through the fetch interceptor, which adds ?as_teacher=
// to /api/teacher-signups — so this only needs to refetch when the previewed teacher changes.
const { fetchApi } = useApi()
const { viewAsTeacher } = useDemoMode()
const { badges } = useTeacherBadges()

const VIEW_KEY = 'teacherSignupsView'
const view = ref((() => { try { return localStorage.getItem(VIEW_KEY) || 'open' } catch { return 'open' } })())
const signups = ref([])
const isLoading = ref(false)
const error = ref(false)

const GROUPS = [
 { key: 'needs_response', title: 'Needs your response' },
 { key: 'waiting', title: 'In progress' },
 { key: 'scheduled', title: 'Scheduled' },
 { key: 'waitlist', title: 'Waitlist' },
]

const groups = computed(() =>
 GROUPS.map(g => ({ ...g, items: signups.value.filter(s => s.group === g.key) })).filter(g => g.items.length)
)

async function load() {
 isLoading.value = true
 error.value = false
 try {
  const data = await fetchApi(`/api/teacher-signups?view=${view.value}`)
  signups.value = data?.signups || []
 } catch {
  error.value = true
 } finally {
  isLoading.value = false
 }
}

watch(view, v => { try { localStorage.setItem(VIEW_KEY, v) } catch { /* storage unavailable */ } load() })
watch(viewAsTeacher, load)
onMounted(load)
</script>
